import { useEffect, useState } from "react";
import { fetchCmdbToolsByOrg } from "../itCompliance/services";
import { collectOrgScopeByType } from "../itCompliance/utils";
import type { ScopeTreeResult } from "../itCompliance/types";

export interface CmdbToolOptionsResult {
  /** Full sorted list of unique tool names from the API. */
  tools: string[];
  /** Draft selection (mirrors the filter bar — not yet applied). */
  draftTool: string[];
  setDraftTool: (tools: string[]) => void;
  /** Applied selection (what the data-fetch hooks actually filter on). */
  appliedTool: string[];
  setAppliedTool: (tools: string[]) => void;
  /** True once the initial fetch has settled (success or error). */
  toolsSeeded: boolean;
}

/**
 * Fetches the unique tool names accessible to the current user (via the
 * CMDB by-org endpoint) and seeds both draft and applied selections with
 * the full list so everything starts selected.
 *
 * Waits for the scope tree to finish loading before firing so there is
 * exactly one request on mount — not one before the tree resolves and
 * another after.
 */
export function useCmdbToolOptions(scopeTree: ScopeTreeResult): CmdbToolOptionsResult {
  const [tools, setTools] = useState<string[]>([]);
  const [draftTool, setDraftTool] = useState<string[]>([]);
  const [appliedTool, setAppliedTool] = useState<string[]>([]);
  const [toolsSeeded, setToolsSeeded] = useState(false);

  useEffect(() => {
    if (scopeTree.loading) return;
    let cancelled = false;

    async function fetchToolOptions() {
      try {
        const instances = await fetchCmdbToolsByOrg(collectOrgScopeByType(scopeTree.data));
        if (cancelled) return;
        const list = Array.from(new Set(instances.map((t) => t.toolName))).sort((a, b) =>
          a.localeCompare(b),
        );
        setTools(list);
        setDraftTool(list);
        setAppliedTool(list);
      } catch (err: unknown) {
        console.error("Agent health tools fetch failed:", err);
      } finally {
        if (!cancelled) setToolsSeeded(true);
      }
    }

    fetchToolOptions();
    return () => {
      cancelled = true;
    };
  }, [scopeTree.loading, scopeTree.data]);

  return { tools, draftTool, setDraftTool, appliedTool, setAppliedTool, toolsSeeded };
}
